import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';

interface UserIntentChartProps {
  userIntentCounts: Record<string, number>;
  isLoading: boolean;
}

export const UserIntentChart = ({ userIntentCounts, isLoading }: UserIntentChartProps) => {
  const { t } = useTranslation('insights_page');

  const pieData = useMemo(() => {
    const MAX_SLICES = 7;
    const entries = Object.entries(userIntentCounts).map(([key, val]) => ({
      name: t(`user_intent.${key}`, key.charAt(0).toUpperCase() + key.slice(1)),
      value: Number(val ?? 0),
    }));
    const sorted = entries.sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, MAX_SLICES);
    const rest = sorted.slice(MAX_SLICES);
    const othersTotal = rest.reduce(
      (sum, it) => sum + (Number.isFinite(it.value) ? it.value : 0),
      0,
    );
    const final =
      othersTotal > 0
        ? [...top, { name: t('user_intent.other', 'Other'), value: othersTotal }]
        : top;
    return final.map((item, idx) => ({ ...item, fill: `var(--chart-primary-${(idx % 10) + 1})` }));
  }, [userIntentCounts, t]);

  const legendValueMap = useMemo(
    () => Object.fromEntries(pieData.map((d) => [d.name, d.value])),
    [pieData],
  );

  const pieConfig = useMemo(
    () => Object.fromEntries(pieData.map((d) => [d.name, { label: d.name }] as const)),
    [pieData],
  );

  const legendPayload = useMemo(
    () => pieData.map((d) => ({ value: d.name, name: d.name, color: d.fill }) as any),
    [pieData],
  );

  const hasData = pieData.length > 0 && pieData.some((d) => d.value > 0);

  return (
    <Card className={isLoading ? 'animate-pulse' : ''}>
      <CardHeader>
        <CardTitle>{t('user_intent.title')}</CardTitle>
        <CardDescription>{t('user_intent.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <ChartPlaceholder
            icon="mingcute:sad-line"
            titleTranslationKey="no_data_in_range.title"
            descriptionTranslationKey="no_data_in_range.description"
            spacing="small"
          />
        ) : (
          <div className="grid grid-cols-[2fr,1fr] gap-6 items-start">
            <ChartContainer
              config={pieConfig as any}
              className="mx-auto h-[250px] w-[250px] min-w-0"
            >
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" stroke="none" innerRadius={60}>
                  {pieData.map((entry, idx) => (
                    <Cell key={`cell-intent-${idx}`} fill={entry.fill as string} />
                  ))}
                </Pie>
                <ChartTooltip
                  cursor={false}
                  wrapperStyle={{ whiteSpace: 'normal' }}
                  content={<ChartTooltipContent hideLabel nameKey="name" className="w-auto" />}
                />
              </PieChart>
            </ChartContainer>
            <div className="pl-2">
              <ChartContainer config={pieConfig as any} fluidHeight className="h-[250px]">
                <ChartLegendContent
                  nameKey="name"
                  valueMap={legendValueMap}
                  payload={legendPayload as any}
                  className="flex-col items-start justify-start gap-2"
                />
              </ChartContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
